import type { LiveReactionKind } from './types';

function variation(id: string) {
  let seed = 2166136261;
  for (let i = 0; i < id.length; i++) seed = Math.imul(seed ^ id.charCodeAt(i), 16777619);
  return (slot: number) => { let x = Math.imul(seed ^ slot * 374761393, 668265263); x = Math.imul(x ^ x >>> 13, 1274126177); return ((x ^ x >>> 16) >>> 0) / 4294967296; };
}
export function reactionPose(kind: LiveReactionKind, id: string, age: number, from: { x: number; y: number }, to: { x: number; y: number }) {
  const random = variation(id); const flight = ({ rose: 900, egg: 620, like: 680, dislike: 780 }[kind]) + random(1) * 170;
  const t = Math.max(0, Math.min(1, age / flight)); const u = 1 - t; const dy = from.y - to.y;
  const sway = (random(2) - .5) * (kind === 'rose' ? 115 : 70);
  const c1 = { x: from.x + sway, y: from.y - dy * .4 - (kind === 'egg' ? 50 : 12) };
  const c2 = { x: to.x - sway * .65, y: to.y + dy * .2 - (kind === 'egg' ? 35 : 0) };
  const x = u ** 3 * from.x + 3 * u * u * t * c1.x + 3 * u * t * t * c2.x + t ** 3 * to.x;
  const y = u ** 3 * from.y + 3 * u * u * t * c1.y + 3 * u * t * t * c2.y + t ** 3 * to.y;
  const impact = Math.max(0, Math.min(1, (age - flight) / 450));
  const wave = Math.sin(t * Math.PI * (2 + random(3) * 2));
  const rotation = kind === 'egg' ? Math.PI * 2 * t * (random(4) > .5 ? 1 : -1) : kind === 'dislike' ? wave * .7 : wave * .35;
  return { x: Math.max(20, Math.min(370, x)), y: y - (kind === 'like' ? Math.abs(wave) * 13 * u : 0), rotation,
    size: 34 + random(5) * 8 + Math.sin(t * Math.PI) * 9, impact, landed: age >= flight, alpha: 1 - impact, random };
}
export function reactionFlight(c: CanvasRenderingContext2D, kind: LiveReactionKind, id: string, age: number, from: { x: number; y: number }, to: { x: number; y: number }) {
  const pose = reactionPose(kind, id, age, from, to); if (pose.alpha <= 0) return;
  const colors = { rose: '#ED709B', egg: '#EAB843', like: '#EEAC51', dislike: '#729FC3' };
  c.save(); c.globalAlpha = pose.alpha;
  if (!pose.landed) {
    for (let i = 3; i >= 1; i--) {
      const tail = reactionPose(kind, id, Math.max(0, age - i * 32), from, to);
      c.globalAlpha = .12 * (1 - i / 4); c.fillStyle = colors[kind]; c.beginPath(); c.arc(tail.x, tail.y, 6 - i, 0, Math.PI * 2); c.fill();
    }
    c.globalAlpha = pose.alpha; c.translate(pose.x, pose.y); c.rotate(pose.rotation);
    c.shadowColor = colors[kind]; c.shadowBlur = 9; reactionIcon(c, kind, 0, 0, pose.size);
  } else {
    const p = pose.impact;
    c.strokeStyle = colors[kind]; c.lineWidth = 2.5 * (1 - p); c.beginPath(); c.arc(to.x, to.y, 17 + p * 32, 0, Math.PI * 2); c.stroke();
    if (kind === 'egg') { c.globalAlpha = pose.alpha * .65; c.fillStyle = '#FFCF50'; c.beginPath(); c.ellipse(to.x, to.y, 16 + p * 16, 10 + p * 8, -.25, 0, Math.PI * 2); c.fill(); }
    else reactionIcon(c, kind, to.x, to.y - Math.sin(p * Math.PI) * (kind === 'dislike' ? -9 : 16), 39 + Math.sin(p * Math.PI) * 13);
    c.globalAlpha = pose.alpha;
    for (let i = 0; i < 12; i++) {
      const a = i * Math.PI / 6 + pose.random(10 + i) * .6; const distance = 15 + p * (22 + pose.random(30 + i) * 22);
      const x = to.x + Math.cos(a) * distance; const y = to.y + Math.sin(a) * distance + p * p * (kind === 'rose' ? 15 : 7);
      c.fillStyle = kind === 'egg' ? i % 2 ? '#FFF9E8' : '#E3C496' : colors[kind]; c.beginPath();
      if (kind === 'rose') c.ellipse(x, y, 5, 2.5, a + p * 2, 0, Math.PI * 2);
      else if (kind === 'egg') { c.moveTo(x - 4, y - 3); c.lineTo(x + 4, y); c.lineTo(x, y + 5); c.closePath(); }
      else { c.moveTo(x, y - 4); c.lineTo(x + 2, y - 1); c.lineTo(x + 5, y); c.lineTo(x + 1, y + 2); c.lineTo(x, y + 5); c.lineTo(x - 2, y + 1); c.lineTo(x - 4, y); c.lineTo(x - 1, y - 1); c.closePath(); }
      c.fill();
    }
  }
  c.restore();
}

/** Native canvas art: no emoji font, remote asset or drawing-document side effect. */
export function reactionIcon(c: CanvasRenderingContext2D, kind: LiveReactionKind, x: number, y: number, size = 22) {
  c.save(); c.translate(x, y); c.scale(size / 24, size / 24); c.lineCap = 'round'; c.lineJoin = 'round'; c.lineWidth = 1.7;
  if (kind === 'rose') {
    c.strokeStyle = '#438762'; c.beginPath(); c.moveTo(0, 1); c.quadraticCurveTo(3, 8, -2, 12); c.stroke();
    c.fillStyle = '#68A775'; c.beginPath(); c.ellipse(4, 6, 5, 2.5, -.55, 0, Math.PI * 2); c.fill();
    for (let i = 0; i < 5; i++) { const a = i * Math.PI * 2 / 5; c.fillStyle = i % 2 ? '#EC7891' : '#C8496A'; c.beginPath(); c.ellipse(Math.cos(a) * 4, -5 + Math.sin(a) * 4, 5, 4, a, 0, Math.PI * 2); c.fill(); }
    c.strokeStyle = '#912C50'; c.beginPath(); c.arc(0, -5, 3, .3, 5.5); c.stroke();
  } else if (kind === 'egg') {
    c.fillStyle = '#FFF6D9'; c.strokeStyle = '#C6A66F'; c.beginPath(); c.moveTo(0, -12); c.bezierCurveTo(9, -12, 15, 11, 0, 11); c.bezierCurveTo(-15, 11, -9, -12, 0, -12); c.fill(); c.stroke();
    c.strokeStyle = '#E9CF9F'; c.beginPath(); c.moveTo(-4, -5); c.lineTo(-6, 0); c.stroke();
  } else {
    if (kind === 'dislike') c.rotate(Math.PI);
    c.fillStyle = kind === 'like' ? '#F0BD75' : '#9AB5C8'; c.strokeStyle = kind === 'like' ? '#A76E35' : '#56768C';
    c.beginPath(); c.moveTo(-5, 0); c.lineTo(0, -5); c.lineTo(1, -12); c.quadraticCurveTo(7, -13, 5, -3); c.lineTo(10, -3); c.quadraticCurveTo(13, -2, 11, 3); c.lineTo(8, 10); c.lineTo(-5, 10); c.closePath(); c.fill(); c.stroke();
    c.fillStyle = '#FFFFFF'; c.fillRect(-11, 0, 5, 10); c.strokeRect(-11, 0, 5, 10);
  }
  c.restore();
}

export function countdownEdges(c: CanvasRenderingContext2D, height: number, remaining: number, now: number, width = 390) {
  if (!Number.isFinite(remaining) || remaining > 10_000 || height <= 0) return;
  const urgency = 1 - Math.max(0, remaining) / 10_000;
  const breath = (1 + Math.sin(now / 1200 * Math.PI * 2)) / 2;
  // A visible base at 10s; never fade completely out between breaths or before reveal.
  const strength = (.22 + .22 * urgency) * (.65 + .35 * breath);
  const edge = Math.min(52 + urgency * 12, height / 4);
  c.save(); c.globalAlpha = 1; c.globalCompositeOperation = 'source-over';
  for (const [x1, y1, x2, y2, x, y, w, h] of [[0, 0, edge, 0, 0, 0, edge, height], [width, 0, width - edge, 0, width - edge, 0, edge, height], [0, 0, 0, edge, 0, 0, width, edge], [0, height, 0, height - edge, 0, height - edge, width, edge]]) {
    const g = c.createLinearGradient(x1, y1, x2, y2);
    g.addColorStop(0, `rgba(224,72,76,${strength})`);
    g.addColorStop(.45, `rgba(224,72,76,${strength * .45})`);
    g.addColorStop(1, 'rgba(224,72,76,0)'); c.fillStyle = g; c.fillRect(x, y, w, h);
  }
  c.restore();
}
